// Typed content definitions (docs/08 §2.3). Effect hooks are tiny pure functions over a ctx.
import type { Decimal } from '../sim/decimal';
import type { Modifiers, SymbolGate } from '../sim/modifiers';
import type { Rng } from '../sim/rng';
import type { ActiveHex, BonusMode, Cell, MetaState, PatternId, Rarity, SizeId, SymbolInstance, Tag } from '../sim/types';

// ── Symbols ─────────────────────────────────────────────────────────────

export interface GridCellView {
  cell: Cell;
  inst: SymbolInstance;
  def: SymbolDef;
}

export interface GridView {
  reels: number;
  rows: number;
  at(reel: number, row: number): GridCellView | null;
  neighbors(cell: Cell): GridCellView[];
  /** Cells within Chebyshev distance `r` (excluding the cell itself). */
  within(cell: Cell, r: number): GridCellView[];
  all(): GridCellView[];
}

/** Step 5 (EFFECTS) context: value math only, plus queued permanent effects. */
export interface EvalCtx {
  self: GridCellView;
  grid: GridView;
  hunger: number;
  /** Read-only modifiers (charms that tweak symbol behaviour, e.g. Cat Bell). */
  mods: Modifiers;
  /** Doom 0..6 (Moon). */
  doom: number;
  value(cell: Cell): number;
  add(cell: Cell, n: number): void;
  mul(cell: Cell, x: number): void;
  set(cell: Cell, n: number): void;
  /** Queue a permanent effect, applied when the spin is banked. False when the target is guarded. */
  destroyLater(uid: number, by: string): boolean;
  growLater(uid: number, n: number): void;
  /** On bank, with `chance`, add a new `defId` instance to the deck. */
  spawnLater(defId: string, chance: number, by: string): void;
}

/** Step 6+ (after patterns) context. */
export interface PostCtx {
  self: GridCellView;
  inPayingPattern: boolean;
  grantFreeCharm(): void;
  /** Change Hunger by n when the spin is banked. */
  hungerLater(n: number): void;
}

/** Step 4 (LAND) context: permanent effects, applied once per real spin (not for nudges or dry-runs). */
export interface LandCtx {
  self: GridCellView;
  grid: GridView;
  windowN: number;
  rng: Rng;
  /** Read-only modifiers. */
  mods: Modifiers;
  /** Apply a random hex from the unlocked pool (Hex symbol). */
  applyHex(by: string): void;
  /** Remove one active hex (Salt). */
  removeHex(by: string): void;
  /** Change Doom (Halo: −1). */
  addDoom(n: number): void;
  addSpins(n: number, by: string, cap: { key: string; max: number }): void;
  freeHold(reel: number): void;
  transform(inst: SymbolInstance, to: string): void;
  transformRandom(inst: SymbolInstance, filter: { rarity?: Rarity; tag?: Tag }): void;
  grow(inst: SymbolInstance, n: number): void;
  ageBy(inst: SymbolInstance, n: number): void;
}

export interface SymbolDef {
  id: string;
  tags: Tag[];
  /** null → never appears in drafts (created by effects only). */
  rarity: Rarity | null;
  baseValue: number;
  sprite: string;
  gate: SymbolGate;
  wild?: boolean;
  /** Pays its VALUE even outside a pattern. */
  offPattern?: boolean;
  sigil?: BonusMode;
  /** +Luck while visible. */
  luck?: number;
  /** ×MULT on patterns containing it. */
  patternX?: number;
  /** Rolled VALUE per appearance [min, max]. */
  roll?: [number, number];
  countsAs?: string[];
  /** Adjacent symbols can't be destroyed or eaten (Spider). */
  guard?: boolean;
  /** Flat and family VALUE modifiers don't apply (Rotten fruit is worth 0). */
  inert?: boolean;
  /** Rot (after G_ROT): after ROT_AGE appearances this instance becomes `rotsInto`. */
  rotsInto?: string;
  /** Rotten fruit: the fresh fruit it came from (Snake Oil matches it as that). */
  rottenFrom?: string;
  /** Counts as wild only for these symbol ids (Bone: skull and tooth patterns). */
  wildFor?: string[];
  /** 3+ of these in one paying pattern trigger CLEANSE (Hex). */
  cleanse?: boolean;
  /** Never offered by drafts even though it has a rarity (story/deal-only symbols). */
  noDraft?: boolean;
  /** Blocks any Devour this spin while visible (Halo). */
  blocksDevour?: boolean;
  /** A Devour caused by a pattern containing it pays ×3 instead of 0 (Golden Tooth). */
  goldenDevour?: boolean;
  land?: (ctx: LandCtx) => void;
  add?: (ctx: EvalCtx) => void;
  mul?: (ctx: EvalCtx) => void;
  copy?: (ctx: EvalCtx) => void;
  post?: (ctx: PostCtx) => void;
}

export function defineSymbol(d: Omit<SymbolDef, 'sprite'> & { sprite?: string }): SymbolDef {
  return { ...d, sprite: d.sprite ?? `sym_${d.id}` };
}

export function isLike(def: SymbolDef, id: string): boolean {
  return def.id === id || (def.countsAs?.includes(id) ?? false);
}

// ── Patterns ────────────────────────────────────────────────────────────

export interface PatternDef {
  id: PatternId;
  /** Smaller patterns of the same family fully contained in a paying one are suppressed. */
  family: string;
  jackpot?: boolean;
  /** Returns every instance of this pattern for a grid size (empty → not available). */
  geometry(reels: number, rows: number, size: SizeId): Cell[][];
}

export function definePattern(d: PatternDef): PatternDef {
  return d;
}

// ── Memory Web nodes ────────────────────────────────────────────────────

export type Branch = 'root' | 'gate' | 'fortune' | 'greed' | 'hands' | 'appetite' | 'rituals' | 'contracts' | 'hand';

export interface NodeDef {
  id: string;
  kind: 'root' | 'gate' | 'stat' | 'hand';
  branch: Branch;
  maxLevel: number;
  baseCost: number;
  /** Extra prerequisites that must ALL be owned (besides one adjacent owned node). */
  requiresAll?: string[];
  /** Also needs this tribute reached in some run (Molt: 15). */
  requiresMaxTribute?: number;
  mods?: (m: Modifiers, level: number) => void;
}

export function defineNode(d: NodeDef): NodeDef {
  return d;
}

// ── Charms ──────────────────────────────────────────────────────────────

export interface CharmSpinCtx {
  spinOfWindow: number;
}
export interface CharmBankCtx {
  payout: Decimal;
  store(key: string, amount: Decimal): void;
}
export interface CharmWindowEndCtx {
  take(key: string): Decimal;
}
export interface CharmRunCtx {
  /** Symbols destroyed this run. */
  destroyed: number;
  flags: Record<string, number>;
}
export interface CharmEvalCtx {
  grid: GridView;
  spinIndex: number;
  /** The machine's current mood id. */
  mood: string;
}

export interface CharmDef {
  id: string;
  rarity: Rarity;
  sprite: string;
  /** † Cursed: never in the shop; comes from Propositions and Reckonings. */
  cursed?: boolean;
  /** Only offered in the shop while this holds (e.g. its system is unlocked). */
  requires?: (m: Modifiers) => boolean;
  mods?: (m: Modifiers) => void;
  /** Modifiers that depend on run memory (Bone Saw, Blood Bank). */
  runMods?: (m: Modifiers, ctx: CharmRunCtx) => void;
  /** ×MULT that depends on the grid or mood this spin (Seventh Seal, The Maw's Favor). */
  evalXMult?: (ctx: CharmEvalCtx) => number;
  spinLuck?: (ctx: CharmSpinCtx) => number;
  onBank?: (ctx: CharmBankCtx) => void;
  /** Returns coins to add at window end (before the tribute is paid). */
  onWindowEnd?: (ctx: CharmWindowEndCtx) => Decimal | null;
}

export function defineCharm(d: Omit<CharmDef, 'sprite'> & { sprite?: string }): CharmDef {
  return { ...d, sprite: d.sprite ?? `charm_${d.id}` };
}

// ── Collectors (Reckonings, docs/04 §16, docs/05 §5) ─────────────────────

export interface CollectorRuleCtx {
  /** Spins already taken this window (before the current pull). */
  spinsUsed: number;
}

export interface CollectorDef {
  id: string;
  /** Tribute multiplier for its window (default: RECKONING_TRIBUTE_MULT, lowered by Debt Relief). */
  tributeMult?: number;
  /** The final boss: never drawn at random. */
  final?: boolean;
  /** Can be rolled as one of the Last Supper's per-spin rules. */
  perSpin?: boolean;
  /** The Last Supper: a random per-spin rule applies (shown before each pull). */
  rollsRules?: boolean;
  /** The tribute is split in two halves, the first due at half the spins (The Twins). */
  split?: boolean;
  /** Every spin applies a random hex (The Hex Widow). */
  hexEachSpin?: boolean;
  /** The right half of the grid mirrors the left half (The Mirror Man). */
  mirror?: boolean;
  /** The rule while it is in force. */
  mods?: (m: Modifiers, ctx: CollectorRuleCtx) => void;
}

export function defineCollector(d: CollectorDef): CollectorDef {
  return d;
}

// ── Hexes (docs/04 §13, docs/05 §6) ──────────────────────────────────────

export interface HexDef {
  id: string;
  /** Only in the pool while this holds (its system is unlocked). */
  requires?: (m: Modifiers) => boolean;
  /** Picks a reel when applied (Seized Reel). */
  targetsReel?: boolean;
  mods?: (m: Modifiers, hex: ActiveHex) => void;
}

export function defineHex(d: HexDef): HexDef {
  return d;
}

// ── Propositions (docs/04 §15, docs/05 §7) ───────────────────────────────

export interface DealCtx {
  /** T(n+1) for the next window. */
  nextTribute: Decimal;
  addCoins(amount: Decimal): void;
  addSymbol(defId: string, count: number): void;
  /** Duplicate the highest-VALUE deck symbol `count` times. Returns its defId. */
  duplicateBest(count: number): string | null;
  /** Destroy `count` random deck symbols (never below MIN_DECK). */
  destroyRandom(count: number, by: string): void;
  setHunger(value: number): void;
  addDoom(n: number): void;
  clearHexes(): void;
  giveCharm(rarity: Rarity): void;
  startNightShift(spins: number): void;
  /** Run-scoped flag / counter (see RunState.flags). */
  flag(key: string, value: number): void;
  nextWindowSpins(delta: number): void;
}

export interface DealReadCtx {
  mods: Modifiers;
  meta: MetaState;
  /** The run has free charm slots / a shop etc. */
  hasShop: boolean;
}

export interface DealSide {
  now?: (ctx: DealCtx) => void;
  mods?: (m: Modifiers) => void;
}

export interface DealDef {
  id: string;
  requires?: (ctx: DealReadCtx) => boolean;
  boon: DealSide;
  fine: DealSide;
  /** The story-forced first proposition. */
  story?: boolean;
  /** The Covenant (needs dealsAccepted ≥ COVENANT_DEALS). */
  covenant?: boolean;
}

export function defineDeal(d: DealDef): DealDef {
  return d;
}

// ── Relics (docs/04 §20, docs/05 §4) ─────────────────────────────────────

export interface RelicDef {
  id: string;
  /** Price in Teeth. */
  cost: number;
  sprite: string;
  /** Only offered while this holds (path relics). */
  requires?: (meta: MetaState) => boolean;
  mods?: (m: Modifiers) => void;
}

export function defineRelic(d: Omit<RelicDef, 'sprite'> & { sprite?: string }): RelicDef {
  return { ...d, sprite: d.sprite ?? `relic_${d.id}` };
}
